import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import * as THREE from "three";
import { Canvas } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";
import { tokens } from "../styles/tokens";
import { fetchSlotCloudBuffer, listSlotClouds } from "../api/rest";
import { parsePcd, heightColors, type Pcd } from "./pcd";
import styles from "./SlotCloud.module.css";

/**
 * ADR 284/286: the slot point-cloud section of the bin inspector — the
 * stitched dual-camera capture of what stands at the address, colored by
 * height on the heat ramp. Renders NOTHING when the address has no cloud
 * (ADR 227/272 rule: an empty evidence block is dead space).
 *
 * Data rail (ADR 286): the tenant API — the newest cloud of the address from
 * `/slot-clouds`, bytes fetched authenticated. In DEV only, an address with
 * no API cloud falls back to the gitignored `/dev-pcd/{address}.pcd` fixture
 * so a fresh checkout still demos JSL. Either way `parsePcd` is the gate:
 * anything that isn't a binary xyz PCD renders as absent, never garbled.
 */
export function SlotCloudSection({ siteId, warehouseId, address }: {
  siteId: string; warehouseId: string; address: string;
}) {
  const { t } = useTranslation();
  const clouds = useQuery({
    queryKey: ["slot-clouds", siteId, warehouseId, address],
    queryFn: () => listSlotClouds(siteId, warehouseId, { address }),
    enabled: !!siteId && !!warehouseId && !!address,
    staleTime: 60_000,
  });
  const lead = clouds.data?.[0] ?? null;
  // Keyed by what was fetched and compared at render (ADR 136 — no
  // synchronous reset in the effect): a stale result is simply ignored.
  const key = `${siteId}/${warehouseId}/${address}`;
  const [result, setResult] = useState<{ key: string; id: string | null; pcd: Pcd } | null>(null);

  const settled = clouds.isSuccess || clouds.isError;
  useEffect(() => {
    if (!settled) return;
    let gone = false;
    const id = lead?.id ?? null;
    const bytes = id
      ? fetchSlotCloudBuffer(siteId, warehouseId, id)
      : import.meta.env.DEV
        ? fetch(`/dev-pcd/${encodeURIComponent(address)}.pcd`).then((r) => (r.ok ? r.arrayBuffer() : null))
        : Promise.resolve(null);
    bytes
      .then((buf) => {
        const pcd = buf ? parsePcd(buf) : null;
        if (!gone && pcd && pcd.count > 0) setResult({ key, id, pcd });
      })
      .catch(() => { /* no cloud for this address — section stays absent */ });
    return () => { gone = true; };
  }, [settled, lead?.id, siteId, warehouseId, address, key]);

  const pcd = result && result.key === key && result.id === (lead?.id ?? null) ? result.pcd : null;
  if (!pcd) return null;
  return (
    <div className={styles.section}>
      <span className={styles.title}>{t("slotCloud.title")}</span>
      <div className={styles.viewer}>
        <Canvas
          gl={{ antialias: false, powerPreference: "low-power" }}
          camera={{ position: [0, -4, 2], up: [0, 0, 1], near: 0.05, far: 100, fov: 50 }}
          onCreated={(s) => { s.gl.setClearColor(tokens.scene.bgBottom); }}
        >
          <CloudPoints pcd={pcd} />
          <OrbitControls makeDefault enableDamping={false} />
        </Canvas>
      </div>
      <span className={styles.hint}>{t("slotCloud.hint", { points: pcd.count.toLocaleString() })}</span>
    </div>
  );
}

function CloudPoints({ pcd }: { pcd: Pcd }) {
  const geometry = useMemo(() => {
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.BufferAttribute(pcd.positions, 3));
    g.setAttribute("color", new THREE.BufferAttribute(heightColors(pcd.positions, tokens.heat), 3));
    // Center the cloud on its own bounding box so the orbit pivot is the cargo,
    // not the camera origin two meters away.
    g.computeBoundingBox();
    const c = new THREE.Vector3();
    g.boundingBox!.getCenter(c);
    g.translate(-c.x, -c.y, -c.z);
    return g;
  }, [pcd]);
  useEffect(() => () => geometry.dispose(), [geometry]);
  return (
    <points geometry={geometry}>
      <pointsMaterial size={0.015} vertexColors sizeAttenuation depthWrite={false} />
    </points>
  );
}
